import { useQuery } from "@tanstack/react-query";
import { type Address } from "viem";

import { AJNA_POOL_ABI } from "@/config/ajna-abi";
import { VENUES } from "@/config/contracts";
import { buildPublicClient } from "@/lib/wallet/clients";
import { useAjnaPositionAddress } from "./use-ajna-position-address";

export type AjnaBorrowerData = {
  /** Borrower's debt at time-0, scaled by inflator → current debt. */
  readonly t0debt: bigint;
  /** Borrower's pledged collateral, 1e18. */
  readonly collateral: bigint;
  /** Borrower-specific neutral-price / threshold-price ratio, 1e18. */
  readonly npTpRatio: bigint;
  /** The AjnaPosition sub-account address for this user. */
  readonly positionAddress: Address;
};

export const useAjnaBorrower = (account: Address | undefined) => {
  const positionQuery = useAjnaPositionAddress(account);
  const positionAddress = positionQuery.data;

  return useQuery({
    queryKey: ["ajna-borrower", VENUES.ajna.contract, positionAddress],
    enabled: Boolean(positionAddress),
    queryFn: async (): Promise<AjnaBorrowerData> => {
      const client = buildPublicClient();
      const tuple = await client.readContract({
        address: VENUES.ajna.contract,
        abi: AJNA_POOL_ABI,
        functionName: "borrowerInfo",
        args: [positionAddress as Address],
      });
      return { t0debt: tuple[0], collateral: tuple[1], npTpRatio: tuple[2], positionAddress: positionAddress as Address };
    },
  });
};
